import React, { useState, Component } from "react";
import { toast } from "sonner";
import {
    Crosshair,
    ArrowsClockwise,
    Sparkle,
    Copy,
    TrendUp,
    TrendDown,
    CaretDown,
    Warning,
} from "@phosphor-icons/react";
import { AdsImportBar } from "@/components/AdsImportBar";
import { adsSurgicalTargeting, adsSurgicalAnalyze, errText } from "@/lib/api";

// Local ErrorBoundary — surfaces the exact error instead of blanking the
// screen. Wraps the SurgicalTargeting body so any render-time bug shows a
// diagnostic panel the operator can copy verbatim into a bug report.
class SurgicalErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { error: null, info: null };
    }
    static getDerivedStateFromError(error) {
        return { error };
    }
    componentDidCatch(error, info) {
        // eslint-disable-next-line no-console
        console.error("SurgicalTargeting crashed:", error, info);
        this.setState({ error, info });
    }
    render() {
        if (this.state.error) {
            return (
                <div className="p-6 max-w-3xl w-full" data-testid="surgical-error-boundary">
                    <div className="border-2 border-red-500 bg-red-50 p-4">
                        <div className="flex items-center gap-2 mb-2">
                            <Warning size={16} weight="bold" className="text-red-700" />
                            <div className="font-heading font-black uppercase tracking-tight text-red-900">
                                Surgical Targeting hit a rendering error
                            </div>
                        </div>
                        <div className="text-xs text-red-900 mb-2">
                            Copy this text into a bug report so it can be fixed:
                        </div>
                        <pre className="text-[11px] bg-white border border-red-200 p-2 overflow-auto whitespace-pre-wrap">
                            {String(this.state.error?.stack || this.state.error?.message || this.state.error)}
                            {this.state.info?.componentStack || ""}
                        </pre>
                        <button
                            type="button"
                            onClick={() => this.setState({ error: null, info: null })}
                            className="mt-3 brutal-btn bg-ink text-white font-mono uppercase tracking-widest text-[10px] font-bold px-3 h-8"
                        >
                            Dismiss and retry
                        </button>
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}

// Coerce anything (number, numeric string, null) to a finite number or 0.
// Keeps `.toFixed()` from ever throwing when Claude / the API returns
// stringified numbers instead of real numbers.
const num = (v) => {
    if (v === null || v === undefined || v === "") return 0;
    const n = typeof v === "number" ? v : parseFloat(v);
    return Number.isFinite(n) ? n : 0;
};

const fmtMoney = (v, currency) => {
    const c = currency ? `${currency} ` : "";
    return `${c}${num(v).toFixed(2)}`;
};

const fmtNum = (v) => num(v).toFixed(2);

const fmtPct = (v) => {
    if (v === null || v === undefined || v === "" || Number.isNaN(Number(v))) return "—";
    return `${num(v).toFixed(2)}%`;
};

const fmtCpa = (v, currency) => {
    if (v === null || v === undefined || v === "") return "—";
    return fmtMoney(v, currency);
};

// Given an array of blended demographic buckets [{bucket, share_of_cost, ...}]
// return the top-N buckets (skipping "Unattributed" if we have enough real
// ones) so the compact column display doesn't get dominated by the
// "no-demographic-data" spend pool.
const topBuckets = (buckets, n = 3) => {
    if (!Array.isArray(buckets) || !buckets.length) return [];
    // Prefer real demographic buckets first; keep Unattributed as a filler
    // only when there aren't enough real ones OR when it's genuinely
    // dominant (>= 50% share) since that's a signal worth surfacing.
    const real = buckets.filter((b) => b.bucket !== "Unattributed");
    const unattr = buckets.filter((b) => b.bucket === "Unattributed");
    const bigUnattr = unattr.filter((b) => (b.share_of_cost || 0) >= 50);
    const merged = [...bigUnattr, ...real, ...unattr.filter((b) => (b.share_of_cost || 0) < 50)];
    // Sort by share_of_cost desc so the compact display is deterministic.
    return merged.slice(0, n).sort((a, b) => (b.share_of_cost || 0) - (a.share_of_cost || 0));
};

// One-line compact string of top-3 buckets, e.g. "25-34 52% · 35-44 32% · 45-54 16%".
const formatMixLine = (buckets) => {
    const top = topBuckets(buckets, 3);
    if (!top.length) return "—";
    return top
        .map((b) => `${b.bucket} ${num(b.share_of_cost).toFixed(0)}%`)
        .join(" · ");
};

// Deterministic colour palette for the tiny stacked-bar visual. Colours
// are the same across every row so a bucket keeps its identity across
// postcodes. Falls back to a neutral shade for buckets we don't hard-code.
const BUCKET_COLOURS = {
    "18-24": "#0d1b7a",
    "25-34": "#132eb8",
    "35-44": "#3454e2",
    "45-54": "#5b7dff",
    "55-64": "#8fa5ff",
    "65+": "#c1cffa",
    Undetermined: "#a3a3a3",
    Unattributed: "#525252",
    "Top 10%": "#0d1b7a",
    "11-20%": "#132eb8",
    "21-30%": "#3454e2",
    "31-40%": "#5b7dff",
    "41-50%": "#8fa5ff",
    "Lower 50%": "#c1cffa",
};

const bucketColour = (bucket) =>
    BUCKET_COLOURS[bucket] || "#6b7280";

// Full expandable-row detail showing every estimated bucket for age +
// income at this postcode: bucket, est. cost, share %, est. conversions,
// est. CPA. Also shows the stacked bar again at readable size and reprints
// the "estimate" disclosure so operators reading a screenshot of just the
// detail row still see the caveat.
const MixDetail = ({ postcode, ageBuckets, incomeBuckets, totalCost, currency }) => {
    const renderTable = (label, buckets) => {
        if (!Array.isArray(buckets) || !buckets.length) {
            return (
                <div className="text-[11px] text-neutral-500 italic">
                    No estimated {label.toLowerCase()} data for this postcode.
                </div>
            );
        }
        return (
            <div>
                <div className="font-mono uppercase tracking-widest text-[10px] font-bold text-ink mb-1">
                    Estimated {label}
                </div>
                <StackedMixBar buckets={buckets} height={10} />
                <table className="w-full text-[11px] mt-2">
                    <thead className="text-neutral-500 font-mono uppercase tracking-widest text-[9px] border-b border-neutral-300">
                        <tr>
                            <th className="text-left py-1">Bucket</th>
                            <th className="text-right py-1">Est. Cost</th>
                            <th className="text-right py-1">% of Cost</th>
                            <th className="text-right py-1">Est. Conv.</th>
                            <th className="text-right py-1">Est. CPA</th>
                        </tr>
                    </thead>
                    <tbody>
                        {buckets.map((b) => (
                            <tr key={b.bucket} className="border-b border-neutral-200">
                                <td className="py-1 font-mono flex items-center gap-2">
                                    <span
                                        className="inline-block w-3 h-3"
                                        style={{ backgroundColor: bucketColour(b.bucket) }}
                                    />
                                    <span className="text-ink font-bold">{b.bucket}</span>
                                </td>
                                <td className="py-1 text-right font-mono">
                                    {fmtMoney(b.est_cost, currency)}
                                </td>
                                <td className="py-1 text-right font-mono">
                                    {num(b.share_of_cost).toFixed(1)}%
                                </td>
                                <td className="py-1 text-right font-mono">
                                    {fmtNum(b.est_conversions)}
                                </td>
                                <td className="py-1 text-right font-mono">
                                    {fmtCpa(b.est_cpa, currency)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };
    return (
        <div>
            <div className="flex items-baseline gap-3 mb-3">
                <div className="font-heading font-black text-ink text-sm">
                    Full estimated demographic mix — {postcode}
                </div>
                <div className="font-mono uppercase tracking-widest text-[9px] text-neutral-500">
                    total cost {fmtMoney(totalCost, currency)}
                </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {renderTable("Age", ageBuckets)}
                {renderTable("Income", incomeBuckets)}
            </div>
            <div className="mt-3 text-[10px] text-neutral-500 leading-relaxed italic">
                These figures are estimates — Google Ads doesn't report demographics
                per location. Each postcode's mix is weighted from the campaigns that
                spent there, assuming each campaign reaches the same audience mix in
                every location.
            </div>
        </div>
    );
};

// Small horizontal stacked bar for a distribution. Renders inline in the
// compact column and again (larger) inside the expanded row.
const StackedMixBar = ({ buckets, height = 6 }) => {
    if (!Array.isArray(buckets) || !buckets.length) {
        return (
            <div
                className="w-full bg-neutral-200"
                style={{ height }}
                aria-label="No estimated mix data"
            />
        );
    }
    // Guard against zero-total (all shares == 0) so the bar still renders.
    const totalShare =
        buckets.reduce((sum, b) => sum + (num(b.share_of_cost) || 0), 0) || 100;
    return (
        <div
            className="flex w-full overflow-hidden bg-neutral-200"
            style={{ height }}
            role="img"
            aria-label="Estimated demographic mix"
        >
            {buckets.map((b) => {
                const w = ((num(b.share_of_cost) || 0) / totalShare) * 100;
                if (w <= 0) return null;
                return (
                    <div
                        key={b.bucket}
                        title={`${b.bucket}: ${num(b.share_of_cost).toFixed(1)}%`}
                        style={{
                            width: `${w}%`,
                            backgroundColor: bucketColour(b.bucket),
                        }}
                    />
                );
            })}
        </div>
    );
};

// Aggregate multi-campaign postcode rows into one row per postcode.
// Google's geographic_view returns one row per (postcode, campaign);
// we sum for the headline table but keep the raw rows for the campaign
// drill-down beneath each postcode.
const aggregatePostcodes = (rows) => {
    const bucket = new Map();
    for (const r of rows || []) {
        const key = (r.postcode || r.location || "").trim();
        if (!key) continue;
        let agg = bucket.get(key);
        if (!agg) {
            agg = {
                postcode: r.postcode || "",
                location: r.location || "",
                country_code: r.country_code || "",
                impressions: 0,
                clicks: 0,
                cost: 0,
                conversions: 0,
                campaigns: [],
            };
            bucket.set(key, agg);
        }
        agg.impressions += r.impressions || 0;
        agg.clicks += r.clicks || 0;
        agg.cost += r.cost || 0;
        agg.conversions += r.conversions || 0;
        agg.campaigns.push({
            campaign_id: r.campaign_id,
            campaign: r.campaign,
            cost: r.cost,
            conversions: r.conversions,
            clicks: r.clicks,
        });
    }
    return Array.from(bucket.values()).map((a) => ({
        ...a,
        cost: Math.round(a.cost * 100) / 100,
        conversions: Math.round(a.conversions * 100) / 100,
        cpa: a.conversions > 0 ? Math.round((a.cost / a.conversions) * 100) / 100 : null,
        conv_rate: a.clicks > 0 ? Math.round((a.conversions / a.clicks) * 10000) / 100 : 0,
        primary_campaign: a.campaigns.sort((x, y) => (y.cost || 0) - (x.cost || 0))[0],
    }));
};

// Format a segment for the "Copy Details" button — one clipboard-ready block
// with the exact data the user needs to paste into Google Ads / a report.
// Uses the ESTIMATED (spend-weighted, blended) top-3 buckets — the same
// distribution shown in the compact column and expandable detail.
const formatSegmentForCopy = (seg, currency) => {
    const ageLine = formatMixLine(seg.est_age);
    const incLine = formatMixLine(seg.est_income);
    const lines = [
        `Postcode: ${seg.postcode || seg.location || "—"}`,
        seg.location && seg.location !== seg.postcode ? `Location: ${seg.location}` : null,
        seg.country_code ? `Country: ${seg.country_code}` : null,
        seg.primary_campaign?.campaign
            ? `Primary Campaign: ${seg.primary_campaign.campaign}`
            : null,
        `Est. Age mix: ${ageLine}`,
        `Est. Income mix: ${incLine}`,
        `Clicks: ${seg.clicks ?? 0}`,
        `Cost: ${fmtMoney(seg.cost, currency)}`,
        `Conversions: ${fmtNum(seg.conversions)}`,
        `CPA: ${fmtCpa(seg.cpa, currency)}`,
        `Conv Rate: ${fmtPct(seg.conv_rate)}`,
        seg.ai_tip ? `AI Tip: ${seg.ai_tip}` : null,
        "(demographic mix is estimated — weighted from campaigns that spent here)",
    ].filter(Boolean);
    return lines.join("\n");
};

const CopyButton = ({ text, testId }) => {
    const [copied, setCopied] = useState(false);
    const onClick = async () => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            toast.success("Segment details copied", { duration: 2000 });
            setTimeout(() => setCopied(false), 1600);
        } catch {
            toast.error("Clipboard blocked by browser");
        }
    };
    return (
        <button
            type="button"
            data-testid={testId}
            onClick={onClick}
            className={`brutal-btn font-mono uppercase tracking-widest text-[10px] font-bold px-2 h-7 flex items-center gap-1 border ${
                copied
                    ? "border-emerald-600 bg-emerald-600 text-white"
                    : "border-neutral-400 bg-white text-neutral-700 hover:border-klein hover:text-klein"
            }`}
            title="Copy this segment's details to your clipboard"
        >
            <Copy size={11} weight="bold" />
            {copied ? "Copied" : "Copy Details"}
        </button>
    );
};

const HeatmapCard = ({ title, items, tone, currency, testIdRoot }) => {
    const [expanded, setExpanded] = useState(true);
    const border = tone === "top" ? "border-emerald-500" : "border-red-500";
    const bg = tone === "top" ? "bg-emerald-50" : "bg-red-50";
    const Icon = tone === "top" ? TrendUp : TrendDown;
    if (!items?.length) return null;
    return (
        <div className={`border-2 ${border} ${bg} mb-6`}>
            <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="w-full flex items-center justify-between gap-3 px-4 py-2"
                data-testid={`${testIdRoot}-toggle`}
            >
                <div className="flex items-center gap-2 text-ink">
                    <Icon size={14} weight="bold" />
                    <span className="font-mono uppercase tracking-widest text-[11px] font-bold">
                        {title}
                    </span>
                    <span className="font-mono uppercase tracking-widest text-[10px] text-neutral-500">
                        · {items.length}
                    </span>
                </div>
                <CaretDown
                    size={13}
                    weight="bold"
                    className={`transition-transform ${expanded ? "rotate-180" : ""}`}
                />
            </button>
            {expanded && (
                <div className="border-t border-neutral-300 divide-y divide-neutral-200 bg-white">
                    {items.map((seg, i) => (
                        <div
                            key={`${seg.postcode || seg.location || i}-${i}`}
                            className="p-3 flex items-start justify-between gap-3"
                            data-testid={`${testIdRoot}-row-${i}`}
                        >
                            <div className="flex-1 min-w-0">
                                <div className="font-heading font-black text-ink text-[13px]">
                                    {seg.postcode || seg.location}
                                    {seg.location && seg.postcode !== seg.location && (
                                        <span className="ml-2 font-mono uppercase tracking-widest text-[9px] text-neutral-500">
                                            {seg.location}
                                        </span>
                                    )}
                                </div>
                                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 font-mono text-[11px] text-neutral-700">
                                    <span>
                                        Cost: <b>{fmtMoney(seg.cost, currency)}</b>
                                    </span>
                                    <span>
                                        Conv: <b>{fmtNum(seg.conversions)}</b>
                                    </span>
                                    <span>
                                        CPA: <b>{fmtCpa(seg.cpa, currency)}</b>
                                    </span>
                                    <span>
                                        Conv Rate: <b>{fmtPct(seg.conv_rate)}</b>
                                    </span>
                                </div>
                                {(seg.est_age?.length || seg.est_income?.length ||
                                    seg.estimated_age_top3?.length || seg.estimated_income_top3?.length) && (
                                    <div className="mt-2 space-y-1 font-mono uppercase tracking-widest text-[9px]">
                                        {(seg.est_age?.length || seg.estimated_age_top3?.length) && (
                                            <div className="flex items-center gap-2">
                                                <span className="text-neutral-400 shrink-0">
                                                    Est. Age
                                                </span>
                                                <span className="text-neutral-700">
                                                    {formatMixLine(
                                                        seg.est_age || seg.estimated_age_top3,
                                                    )}
                                                </span>
                                            </div>
                                        )}
                                        {(seg.est_income?.length || seg.estimated_income_top3?.length) && (
                                            <div className="flex items-center gap-2">
                                                <span className="text-neutral-400 shrink-0">
                                                    Est. Income
                                                </span>
                                                <span className="text-neutral-700">
                                                    {formatMixLine(
                                                        seg.est_income || seg.estimated_income_top3,
                                                    )}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                )}
                                {seg.ai_tip && (
                                    <div className="mt-2 text-xs text-neutral-700 border-l-2 border-klein pl-2">
                                        <Sparkle
                                            size={10}
                                            weight="bold"
                                            className="inline-block text-klein mr-1"
                                        />
                                        {seg.ai_tip}
                                    </div>
                                )}
                            </div>
                            <CopyButton
                                testId={`${testIdRoot}-copy-${i}`}
                                text={formatSegmentForCopy(seg, currency)}
                            />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export const SurgicalTargeting = () => (
    <SurgicalErrorBoundary>
        <SurgicalTargetingInner />
    </SurgicalErrorBoundary>
);

const SurgicalTargetingInner = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [heatmap, setHeatmap] = useState(null);
    const [aiLoading, setAiLoading] = useState(false);
    // Persistent error banner. `toast.error` alone fades in 5s and leaves
    // a blank tab body when the fetch fails — hard to tell "empty account"
    // from "backend crashed". Storing the last error string here so the
    // tab always shows something explainable.
    const [fetchError, setFetchError] = useState(null);
    // Which postcode row is currently expanded to show its full est
    // demographic tables. Only one at a time to keep the table compact.
    const [expandedPostcode, setExpandedPostcode] = useState(null);

    const handleFetch = async (customerId, dateRange, account) => {
        setLoading(true);
        setHeatmap(null);
        setFetchError(null);
        try {
            const r = await adsSurgicalTargeting(
                customerId,
                dateRange,
                account?.name || "",
            );
            const agg = aggregatePostcodes(r?.postcodes || []);
            const pcMix = r?.postcode_mix || {};
            const enriched = agg.map((row) => {
                const key =
                    (row.postcode || row.location || "").toString().trim();
                const mix = pcMix[key] || {};
                return {
                    ...row,
                    // Full spend-weighted BLENDED estimate per postcode.
                    // Replaces the old "single campaign's top bucket" story.
                    est_age: Array.isArray(mix.age) ? mix.age : [],
                    est_income: Array.isArray(mix.income) ? mix.income : [],
                };
            });
            const nextData = {
                ...(r || {}),
                account_name: account?.name || r?.account_name || `Account ${customerId}`,
                currency: account?.currency || r?.currency || "",
                aggregated: enriched.sort((a, b) => (b.cost || 0) - (a.cost || 0)),
            };
            setData(nextData);
            if (!enriched.length) {
                toast.info(
                    "No postcode-level data returned. Check that the account has location reports enabled for this date range.",
                );
            }
        } catch (e) {
            const detail = errText(e, "Failed to load surgical report");
            // eslint-disable-next-line no-console
            console.error("Surgical Targeting fetch failed:", e);
            // `errText` always returns a string, so state is always safe to render.
            setFetchError(detail);
            setData(null);
            // Re-throw so AdsImportBar shows the accurate error toast
            // instead of its default "Loaded surgical …" success.
            throw e;
        } finally {
            setLoading(false);
        }
    };

    const runAiHeatmap = async () => {
        if (!data?.aggregated?.length) return;
        setAiLoading(true);
        try {
            const r = await adsSurgicalAnalyze({
                customer_id: data.customer_id,
                account_name: data.account_name || "",
                date_range: data.date_range || "",
                currency: data.currency || "",
                postcodes: data.aggregated.map((row) => ({
                    postcode: row.postcode,
                    location: row.location,
                    country_code: row.country_code,
                    campaign_id: row.primary_campaign?.campaign_id || "",
                    campaign: row.primary_campaign?.campaign || "",
                    impressions: row.impressions,
                    clicks: row.clicks,
                    cost: row.cost,
                    conversions: row.conversions,
                    cpa: row.cpa,
                    conv_rate: row.conv_rate,
                })),
                age_summary: data.age_summary || [],
                income_summary: data.income_summary || [],
                campaign_mix: data.campaign_mix || {},
                // Blended per-postcode estimate — the AI reasons on this
                // (top-3 buckets per dimension) instead of a single
                // campaign's top bucket.
                postcode_mix: data.postcode_mix || {},
            });
            setHeatmap(r?.heatmap || { top: [], bottom: [] });
            toast.success(
                `AI heatmap ready — ${r?.heatmap?.top?.length || 0} winners, ${r?.heatmap?.bottom?.length || 0} losers`,
            );
        } catch (e) {
            const detail = errText(e, "AI heatmap failed");
            toast.error(detail);
        } finally {
            setAiLoading(false);
        }
    };

    const currency = data?.currency ? data.currency.toUpperCase() : "";

    return (
        <div className="flex flex-col flex-1 overflow-auto">
            <div className="px-6 py-4 space-y-4 max-w-7xl w-full">
                <div className="flex items-center gap-2">
                    <Crosshair size={16} weight="bold" className="text-klein" />
                    <h2 className="font-heading font-black text-2xl text-ink">
                        Surgical Targeting
                    </h2>
                </div>
                <p className="text-sm text-neutral-600">
                    Postcode × Age × Household Income overlay. Read-only — works on Explorer-tier
                    dev tokens too. Copy any recommendation to paste straight into Google Ads.
                </p>
                <p
                    className="text-xs text-neutral-500 leading-relaxed border-l-2 border-neutral-300 pl-3"
                    data-testid="surgical-estimate-disclaimer"
                >
                    <b>Estimated:</b> Google Ads doesn't report demographics by location.
                    Each postcode's mix is weighted from the campaigns that spent there,
                    assuming each campaign reaches the same audience mix in every location.
                </p>

                <AdsImportBar reportKind="surgical" onFetch={handleFetch} />

                {fetchError && !loading && (
                    <div
                        className="border-2 border-red-500 bg-red-50 p-4"
                        data-testid="surgical-fetch-error"
                    >
                        <div className="flex items-start gap-3">
                            <Warning
                                size={16}
                                weight="bold"
                                className="text-red-700 mt-0.5 shrink-0"
                            />
                            <div className="flex-1 min-w-0">
                                <div className="font-heading font-black uppercase tracking-tight text-red-900">
                                    Fetch failed
                                </div>
                                <div className="mt-1 text-xs text-red-900 break-words">
                                    {fetchError}
                                </div>
                                <div className="mt-2 text-[11px] text-red-800 leading-relaxed">
                                    Try a shorter date range, confirm the account has
                                    location targeting enabled, or check that your Google
                                    Ads connection is still authorised.
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => setFetchError(null)}
                                className="brutal-btn border border-red-500 bg-white text-red-700 font-mono uppercase tracking-widest text-[10px] font-bold px-2 h-7"
                            >
                                Dismiss
                            </button>
                        </div>
                    </div>
                )}

                {loading && (
                    <div className="border border-neutral-300 bg-neutral-50 p-8 text-center">
                        <ArrowsClockwise
                            size={22}
                            weight="bold"
                            className="text-klein animate-spin mx-auto"
                        />
                        <div className="mt-3 font-mono uppercase tracking-widest text-[10px] font-bold text-ink">
                            Pulling postcode + demographic data from Google Ads…
                        </div>
                    </div>
                )}

                {data && !loading && !(data.aggregated || []).length && (
                    <div className="border-2 border-amber-400 bg-amber-50 p-4 flex gap-3 items-start">
                        <Warning
                            size={16}
                            weight="bold"
                            className="text-amber-700 mt-0.5 shrink-0"
                        />
                        <div className="text-sm text-amber-900">
                            <div className="font-heading font-black uppercase tracking-tight">
                                No postcode data returned
                            </div>
                            <div className="mt-1 text-xs leading-relaxed">
                                Either this account has no active location performance for the
                                selected date range, or postcode-level attribution is disabled.
                                Try a longer date range or confirm the account uses location targeting.
                            </div>
                        </div>
                    </div>
                )}

                {data && (data.aggregated || []).length > 0 && (
                    <>
                        <div className="border-2 border-ink bg-ink text-white p-4 flex items-center justify-between gap-4">
                            <div>
                                <div className="font-mono uppercase tracking-widest text-[10px] text-neutral-400">
                                    Loaded
                                </div>
                                <div className="font-heading font-black text-sm">
                                    {data.account_name}
                                    <span className="text-neutral-400 font-mono ml-2">
                                        · {(data.date_range || "").replace(/_/g, " ").toLowerCase()} ·{" "}
                                        {(data.aggregated || []).length} postcode
                                        {(data.aggregated || []).length === 1 ? "" : "s"}
                                    </span>
                                </div>
                            </div>
                            <button
                                type="button"
                                data-testid="surgical-run-ai"
                                onClick={runAiHeatmap}
                                disabled={aiLoading}
                                className="brutal-btn bg-klein hover:bg-klein/90 text-white font-mono uppercase tracking-widest text-[10px] font-bold px-3 h-9 flex items-center gap-1.5 disabled:opacity-50"
                            >
                                <Sparkle
                                    size={13}
                                    weight="bold"
                                    className={aiLoading ? "animate-spin" : ""}
                                />
                                {aiLoading ? "Analysing…" : "Run AI Heatmap"}
                            </button>
                        </div>

                        {heatmap && (
                            <>
                                <HeatmapCard
                                    title="Top performers — increase bids here"
                                    items={heatmap.top}
                                    tone="top"
                                    currency={currency}
                                    testIdRoot="surgical-heatmap-top"
                                />
                                <HeatmapCard
                                    title="Bottom performers — cut / exclude"
                                    items={heatmap.bottom}
                                    tone="bottom"
                                    currency={currency}
                                    testIdRoot="surgical-heatmap-bottom"
                                />
                            </>
                        )}

                        <div className="border-2 border-ink bg-white" data-testid="surgical-postcode-table">
                            <div className="bg-ink text-white px-4 py-2 font-mono uppercase tracking-widest text-[11px] font-bold flex items-center gap-2">
                                <Crosshair size={13} weight="bold" />
                                Postcode Performance · Overlay
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-neutral-100 border-b-2 border-ink text-neutral-700">
                                        <tr className="font-mono uppercase tracking-widest text-[10px]">
                                            <th className="text-left px-3 py-2 w-8"></th>
                                            <th className="text-left px-3 py-2">Postcode</th>
                                            <th className="text-left px-3 py-2">Location</th>
                                            <th className="text-left px-3 py-2">Top Campaign</th>
                                            <th className="text-left px-3 py-2 min-w-[220px]">Est. Age mix</th>
                                            <th className="text-left px-3 py-2 min-w-[220px]">Est. Income mix</th>
                                            <th className="text-right px-3 py-2">Clicks</th>
                                            <th className="text-right px-3 py-2">Cost</th>
                                            <th className="text-right px-3 py-2">Conv.</th>
                                            <th className="text-right px-3 py-2">CPA</th>
                                            <th className="text-right px-3 py-2">Conv Rate</th>
                                            <th className="text-right px-3 py-2">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(data.aggregated || []).map((row, i) => {
                                            const rowKey =
                                                (row.postcode || row.location || `row-${i}`).toString();
                                            const isExpanded = expandedPostcode === rowKey;
                                            return (
                                                <React.Fragment key={rowKey}>
                                                    <tr
                                                        className="border-b border-neutral-200"
                                                        data-testid={`surgical-row-${i}`}
                                                    >
                                                        <td className="px-3 py-2">
                                                            <button
                                                                type="button"
                                                                onClick={() =>
                                                                    setExpandedPostcode(
                                                                        isExpanded ? null : rowKey,
                                                                    )
                                                                }
                                                                aria-label={
                                                                    isExpanded
                                                                        ? "Collapse row"
                                                                        : "Expand row for full mix"
                                                                }
                                                                title={
                                                                    isExpanded
                                                                        ? "Collapse full mix"
                                                                        : "Expand full mix"
                                                                }
                                                                data-testid={`surgical-expand-${i}`}
                                                                className="brutal-btn border border-neutral-400 bg-white text-neutral-700 hover:bg-neutral-50 w-6 h-6 flex items-center justify-center"
                                                            >
                                                                <CaretDown
                                                                    size={11}
                                                                    weight="bold"
                                                                    className={`transition-transform ${
                                                                        isExpanded ? "rotate-180" : ""
                                                                    }`}
                                                                />
                                                            </button>
                                                        </td>
                                                        <td className="px-3 py-2 font-heading font-black text-ink text-[13px]">
                                                            {row.postcode || "—"}
                                                        </td>
                                                        <td className="px-3 py-2 text-neutral-700 text-[12px]">
                                                            {row.location}
                                                        </td>
                                                        <td className="px-3 py-2 text-neutral-700 text-[12px]">
                                                            {row.primary_campaign?.campaign || "—"}
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            <div className="font-mono text-[11px] text-neutral-700">
                                                                {formatMixLine(row.est_age)}
                                                            </div>
                                                            <div className="mt-1">
                                                                <StackedMixBar
                                                                    buckets={topBuckets(row.est_age, 6)}
                                                                />
                                                            </div>
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            <div className="font-mono text-[11px] text-neutral-700">
                                                                {formatMixLine(row.est_income)}
                                                            </div>
                                                            <div className="mt-1">
                                                                <StackedMixBar
                                                                    buckets={topBuckets(row.est_income, 6)}
                                                                />
                                                            </div>
                                                        </td>
                                                        <td className="px-3 py-2 text-right font-mono text-[12px]">
                                                            {row.clicks}
                                                        </td>
                                                        <td className="px-3 py-2 text-right font-mono text-[12px]">
                                                            {fmtMoney(row.cost, currency)}
                                                        </td>
                                                        <td className="px-3 py-2 text-right font-mono text-[12px]">
                                                            {fmtNum(row.conversions)}
                                                        </td>
                                                        <td className="px-3 py-2 text-right font-mono text-[12px]">
                                                            {fmtCpa(row.cpa, currency)}
                                                        </td>
                                                        <td className="px-3 py-2 text-right font-mono text-[12px]">
                                                            {fmtPct(row.conv_rate)}
                                                        </td>
                                                        <td className="px-3 py-2 text-right">
                                                            <CopyButton
                                                                testId={`surgical-copy-${i}`}
                                                                text={formatSegmentForCopy(row, currency)}
                                                            />
                                                        </td>
                                                    </tr>
                                                    {isExpanded && (
                                                        <tr
                                                            className="border-b-2 border-ink bg-neutral-50"
                                                            data-testid={`surgical-detail-${i}`}
                                                        >
                                                            <td colSpan={12} className="px-3 py-4">
                                                                <MixDetail
                                                                    postcode={row.postcode || row.location}
                                                                    ageBuckets={row.est_age}
                                                                    incomeBuckets={row.est_income}
                                                                    totalCost={row.cost}
                                                                    currency={currency}
                                                                />
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};
